import { Box, Card, CardContent, styled } from '@mui/material';
import { Handle, Node } from '@xyflow/react';
import { NodeProps, Position } from '@xyflow/system';
import { memo, useEffect, useRef } from 'react';

export const StepNodeWidth = 500;

const PREFIX = 'WorkflowStepNode';

const classes = {
  card: `${PREFIX}-card`,
  cardContent: `${PREFIX}-cardContent`,
  cardShadowSelected: `${PREFIX}-cardShadowSelected`,
  header: `${PREFIX}-header`,
  list: `${PREFIX}-list`,
  handle: `${PREFIX}-handle`,
  handleBotRight: `${PREFIX}-handleBotRight`,
  handleBotLeft: `${PREFIX}-handleBotLeft`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.card}`]: {
    width: StepNodeWidth,
    margin: 5,
  },
  [`& .${classes.cardContent}`]: {
    padding: 0,
    paddingBottom: theme.spacing(1),
  },
  [`& .${classes.cardShadowSelected}`]: {
    boxShadow: `0px 0px 10px ${theme.palette.primary.main}`,
  },
  [`& .${classes.header}`]: {
    textAlign: 'center',
    marginTop: 0,
  },
  [`& .${classes.list}`]: {
    marginBottom: 0,
    paddingLeft: theme.spacing(3),
  },
  [`& .${classes.handle}`]: {
    opacity: 0,
  },
  [`& .${classes.handleBotRight}`]: {
    right: 100,
    bottom: 0,
    left: 'auto',
  },
  [`& .${classes.handleBotLeft}`]: {
    left: 100,
    bottom: 0,
    right: 'auto',
  },
}));

export enum HandleId {
  TopCenter = 'top-center',
  RightCenter = 'right-center',
  BottomLeft = 'bottom-left',
  BottomCenter = 'bottom-center',
  BottomRight = 'bottom-right',
}

export type WorkflowStepNodeData = {
  row: number;
  title: string;
  actions: string[];
  parentId?: string;
  onHeightChange?: (id: string, height: number) => void;
};

type WorkflowStepNodeType = Node<WorkflowStepNodeData, 'stepData'>;

const WorkflowStepNode = ({
  id,
  data,
  isConnectable,
  selected,
}: NodeProps<WorkflowStepNodeType>) => {
  const { title, actions } = data;
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (nodeRef.current) {
      const observer = new ResizeObserver(() => {
        const height = nodeRef.current?.offsetHeight;
        data.onHeightChange?.(id, height ?? 0);
      });

      observer.observe(nodeRef.current);

      data.onHeightChange?.(id, nodeRef.current.offsetHeight);

      return () => observer.disconnect();
    }
  }, [id, data]);

  return (
    <StyledBox ref={nodeRef}>
      <Handle
        type="target"
        id={HandleId.TopCenter}
        position={Position.Top}
        isConnectable={isConnectable}
        className={classes.handle}
      />
      <Handle
        type="target"
        id={HandleId.RightCenter}
        position={Position.Right}
        isConnectable={isConnectable}
        className={classes.handle}
      />
      <Card
        variant="outlined"
        className={`${classes.card} ${selected ? classes.cardShadowSelected : ''}`}>
        <CardContent className={classes.cardContent}>
          <h3 className={classes.header}>{title}</h3>
          <ul className={classes.list}>
            {actions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        </CardContent>
      </Card>
      <Handle
        type="source"
        id={HandleId.BottomCenter}
        position={Position.Bottom}
        isConnectable={isConnectable}
        className={classes.handle}
      />
      <Handle
        type="source"
        id={HandleId.BottomRight}
        position={Position.Bottom}
        isConnectable={isConnectable}
        className={`${classes.handle} ${classes.handleBotRight}`}
      />
      <Handle
        type="source"
        id={HandleId.BottomLeft}
        position={Position.Bottom}
        isConnectable={isConnectable}
        className={`${classes.handle} ${classes.handleBotLeft}`}
      />
    </StyledBox>
  );
};

export default memo(WorkflowStepNode);
